import React, { useState, useRef, useEffect } from "react";
import "./ComboListComponent.css";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { 
  FaArrowLeft, 
  FaSearch, 
  FaTimes, 
  FaPlus, 
  FaImage, 
  FaCalendarAlt,
  FaBox,
  FaFilter
} from "react-icons/fa";
import LoadingSpinner from "../Loading/component";
import Sidebar from "../Navigation/Sidebar/Sidebar";

const ComboListComponent = (props) => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(props.searchTerm || "");
  const [showFilters, setShowFilters] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const dropdownRef = useRef(null);

  const handleClick = () => navigate("/");
  const newComboCreation = () => navigate("/home/comboCreation");
  const handleComboDetails = (comboId) => navigate(`/home/combodetails/${comboId}`);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    props.setSearchTerm(value);
    
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer with 500ms delay
    const timer = setTimeout(() => {
      props.SearchCombosApi(value);
    }, 500);
    
    setDebounceTimer(timer);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    props.setSearchTerm("");
    props.SearchCombosApi("");
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClearAllFilter = () => {
    props.handleClearAllFilter();
    setSearchInput("");
    props.setSearchTerm("");
    props.SearchCombosApi("");
    setShowFilters(false);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
  };

  // Count active filters
  const activeFilterCount = (props.comboStatusFilter?.length || 0) + (props.sortOption ? 1 : 0);

  // Stats
  const totalCombos = props.combos?.length || 0;
  const activeCombos = props.combos?.filter(c => c.isActive).length || 0;
  const inactiveCombos = totalCombos - activeCombos;

  return (
    <>
      {props.loading ? (
        <LoadingSpinner />
      ) : (
        <div className="cl-main-wrapper">
          {/* Sidebar */}
          <div className="cl-sidebar-container">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="cl-content-wrapper">
            {/* Header */}
            <div className="cl-header">
              <div className="cl-header-left">
                <FaArrowLeft 
                  onClick={handleClick} 
                  className="cl-back-icon" 
                />
                <h1 className="cl-title">
                  Combo 
                </h1>
              </div>
              
              <div className="cl-header-center">
                <div className="cl-search-wrapper">
                  <FaSearch className="cl-search-icon" />
                  <input
                    type="text"
                    placeholder="Search combo by ID or Name..."
                    value={searchInput}
                    onChange={handleSearchChange}
                    className="cl-search-input"
                  />
                  {searchInput && (
                    <button className="cl-search-clear" onClick={handleClearSearch}>
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              <div className="cl-header-right">
                {/* Filter Button */}
                <div style={{ position: 'relative' }} ref={dropdownRef}>
                  <button 
                    className={`cl-filter-btn ${showFilters ? 'active' : ''}`}
                    onClick={handleToggleFilters}
                  >
                    <FaFilter className="cl-filter-icon" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="cl-filter-badge">{activeFilterCount}</span>
                    )}
                  </button>

                  {/* Filter Dropdown */}
                  {showFilters && (
                    <div className="cl-filter-dropdown">
                      <div className="cl-filter-dropdown-header">
                        <h4 className="cl-filter-dropdown-title">Filters</h4>
                        <button 
                          className="cl-filter-clear-all" 
                          onClick={handleClearAllFilter}
                        >
                          Clear All
                        </button>
                      </div>

                      {/* Status Filters */}
                      <div className="cl-filter-section">
                        <h5 className="cl-filter-section-title">Status</h5>
                        <label className="cl-filter-option">
                          <input
                            type="checkbox"
                            checked={props.comboStatusFilter?.includes("Active") || false}
                            onChange={() => props.toggleComboStatusFilter("Active")}
                          />
                          <span className="cl-filter-option-label">
                            <span className="cl-status-dot active"></span>
                            Active
                          </span>
                        </label>
                        <label className="cl-filter-option">
                          <input
                            type="checkbox"
                            checked={props.comboStatusFilter?.includes("Inactive") || false}
                            onChange={() => props.toggleComboStatusFilter("Inactive")}
                          />
                          <span className="cl-filter-option-label">
                            <span className="cl-status-dot inactive"></span>
                            Inactive
                          </span>
                        </label>
                      </div>

                      {/* Sort Options */}
                      <div className="cl-filter-section">
                        <h5 className="cl-filter-section-title">Sort By</h5>
                        <label className="cl-sort-option">
                          <input
                            type="radio"
                            name="sort"
                            checked={props.sortOption === "priceHighToLow"}
                            onChange={() => props.setSortOption("priceHighToLow")}
                          />
                          Price (High to Low)
                        </label>
                        <label className="cl-sort-option">
                          <input
                            type="radio"
                            name="sort"
                            checked={props.sortOption === "priceLowToHigh"}
                            onChange={() => props.setSortOption("priceLowToHigh")}
                          />
                          Price (Low to High)
                        </label>
                        <label className="cl-sort-option">
                          <input
                            type="radio"
                            name="sort"
                            checked={props.sortOption === "aToZ"}
                            onChange={() => props.setSortOption("aToZ")}
                          />
                          A to Z
                        </label>
                        <label className="cl-sort-option">
                          <input
                            type="radio"
                            name="sort"
                            checked={props.sortOption === "zToA"}
                            onChange={() => props.setSortOption("zToA")}
                          />
                          Z to A
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <button className="cl-create-btn" onClick={newComboCreation}>
                  <FaPlus className="cl-create-icon" />
                  Create Combo
                </button>
              </div>
            </div>

           

            {/* Combo Grid */}
            <div className="cl-grid-container">
              {props.combos && props.combos.length > 0 ? (
                <div className="cl-grid">
                  {props.combos.map((combo) => (
                    <div
                      key={combo.id}
                      className={`cl-card ${!combo.isActive ? 'cl-card-disabled' : ''}`}
                      onClick={() => handleComboDetails(combo.id)}
                    >
                      {/* Status Badge */}
                      {combo.isActive ? (
                        <div className="cl-card-active-badge">Active</div>
                      ) : (
                        <div className="cl-card-active-badge" style={{ background: '#d93025' }}>Inactive</div>
                      )}

                      {/* Image */}
                      <div className="cl-card-image-wrapper">
                        {combo.image_url && combo.image_url.length > 0 ? (
                          <img
                            alt={combo.name}
                            src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${combo.image_url}`}
                            className="cl-card-image"
                          />
                        ) : (
                          <div className="cl-card-image-placeholder">
                            <FaImage />
                          </div>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="cl-card-body">
                        <h4 className="cl-card-title">{combo.name || "Unnamed Combo"}</h4>
                        <p className="cl-card-description">
                          {combo.description || "No description available"}
                        </p>

                        <div className="cl-card-footer">
                          <div className="cl-card-prices">
                            {combo.actual_price && (
                              <p className="cl-card-actual-price">₹{combo.actual_price}</p>
                            )}
                            <p className="cl-card-total-price">₹{combo.total_price || 0}</p>
                          </div>
                          <p className="cl-card-id">ID: {combo.id}</p>
                        </div>

                        {combo.expires_at && (
                          <p className="cl-card-expiry">
                            <FaCalendarAlt className="cl-card-expiry-icon" />
                            Expires: {moment.utc(combo.expires_at).tz("Asia/Kolkata").format("DD MMM YYYY")}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cl-empty-state">
                  <div className="cl-empty-icon">📦</div>
                  <h3 className="cl-empty-title">No Combos Found</h3>
                  <p className="cl-empty-subtitle">
                    {searchInput ? "Try adjusting your search" : "Create your first combo to get started"}
                  </p>
                  {!searchInput && (
                    <button className="cl-empty-btn" onClick={newComboCreation}>
                      <FaPlus />
                      Create Combo
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComboListComponent;